<template>
  <form name="form-place" id="form-place" class="place-form" novalidate @submit="onSubmit" @input="markDirty" @change="markDirty">
    <input type="hidden" name="id" v-model="model.id">
    <input type="hidden" name="image1" v-model="model.image1">
    <input type="hidden" name="image2" v-model="model.image2">
    <input type="hidden" name="image3" v-model="model.image3">

    <p class="form-required-note"><span aria-hidden="true">*</span> Required fields</p>

    <div v-if="Object.keys(fieldErrors).length" class="form-alert" role="alert" aria-live="assertive">
      <strong>Review the highlighted fields.</strong>
      <span>{{ formErrorMessage }}</span>
    </div>

    <div class="form-row place-form-sections">
      <section class="col-lg-6 mb-4 mb-lg-0" aria-labelledby="place-details-heading">
        <div class="place-form-section">
          <div class="section-heading">
            <div>
              <p class="section-eyebrow">PLACE DETAILS</p>
              <h2 id="place-details-heading">Identity and description</h2>
            </div>
            <i class="pi pi-map-marker" aria-hidden="true"></i>
          </div>

          <div class="form-group">
            <label for="name">Name <span class="required-indicator" aria-hidden="true">*</span></label>
            <InputText id="name" v-model="model.name" class="form-control" :class="{ 'p-invalid': fieldErrors.name }" aria-describedby="name-help name-error" required />
            <small id="name-help" class="form-help">Use the name delegates will recognize on the event map and directory.</small>
            <small v-if="fieldErrors.name" id="name-error" class="field-error">{{ fieldErrors.name }}</small>
          </div>

          <div class="form-group">
            <label for="placeTypeId">Type <span class="required-indicator" aria-hidden="true">*</span></label>
            <select id="placeTypeId" class="form-control" v-model="model.placeTypeId" :class="{ 'p-invalid': fieldErrors.placeTypeId }" aria-describedby="place-type-help place-type-error" required>
              <option value="" disabled>Select a type</option>
              <option v-for="data in placeTypes" :key="data.value" :value="data.value">{{ data.label }}</option>
            </select>
            <small id="place-type-help" class="form-help">Choose the category that best describes this place.</small>
            <small v-if="fieldErrors.placeTypeId" id="place-type-error" class="field-error">{{ fieldErrors.placeTypeId }}</small>
          </div>

          <div class="form-group mb-0">
            <label for="description">Description <span class="required-indicator" aria-hidden="true">*</span></label>
            <Textarea id="description" v-model="model.description" class="form-control" :class="{ 'p-invalid': fieldErrors.description }" aria-describedby="description-help description-error" rows="8" required />
            <small id="description-help" class="form-help">Describe the location, access information, and anything delegates should know before visiting.</small>
            <small v-if="fieldErrors.description" id="description-error" class="field-error">{{ fieldErrors.description }}</small>
          </div>
        </div>
      </section>

      <section class="col-lg-6" aria-labelledby="place-location-heading">
        <div class="place-form-section">
          <div class="section-heading">
            <div>
              <p class="section-eyebrow">MAP AND MEDIA</p>
              <h2 id="place-location-heading">Coordinates and image</h2>
            </div>
            <i class="pi pi-image" aria-hidden="true"></i>
          </div>

          <div class="coordinate-heading">
            <span>Map coordinates</span>
            <small>Optional. Enter decimal degrees if the location should appear on a map.</small>
          </div>
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label for="latitude">Latitude</label>
                <InputText id="latitude" v-model="model.latitude" type="number" step="any" class="form-control" :class="{ 'p-invalid': fieldErrors.latitude }" aria-describedby="latitude-error" />
                <small v-if="fieldErrors.latitude" id="latitude-error" class="field-error">{{ fieldErrors.latitude }}</small>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label for="longitude">Longitude</label>
                <InputText id="longitude" v-model="model.longitude" type="number" step="any" class="form-control" :class="{ 'p-invalid': fieldErrors.longitude }" aria-describedby="longitude-error" />
                <small v-if="fieldErrors.longitude" id="longitude-error" class="field-error">{{ fieldErrors.longitude }}</small>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label for="orderNumber">Display order</label>
            <InputText id="orderNumber" v-model="model.orderNumber" type="number" inputmode="numeric" min="0" class="form-control" :class="{ 'p-invalid': fieldErrors.orderNumber }" aria-describedby="order-number-help order-number-error" />
            <small id="order-number-help" class="form-help">Optional. Use a whole number to control the order in which places appear.</small>
            <small v-if="fieldErrors.orderNumber" id="order-number-error" class="field-error">{{ fieldErrors.orderNumber }}</small>
          </div>

          <div class="form-group mb-0">
            <label for="place-image">Image</label>
            <small class="form-help upload-help">Optional. Upload a JPG, PNG, or WEBP image up to 5 MB.</small>
            <FileUpload
                id="place-image"
                mode="advanced"
                name="file"
                :multiple="false"
                accept="image/jpeg,image/png,image/webp"
                :maxFileSize="5242880"
                chooseLabel="Choose image"
                uploadLabel="Upload image"
                cancelLabel="Remove"
                v-model="model.uploadFile"
                :url="uploadUrl"
                @select="markDirty"
                @error="onUploadError"
                @upload="onUploadSuccess"
            />
            <div v-if="imagePreviewUrl" class="place-image-preview">
              <img :src="imagePreviewUrl" alt="Current place image">
              <span>Current image</span>
            </div>
          </div>
        </div>
      </section>
    </div>

    <div class="form-actions">
      <p class="form-actions-note">{{ hasUnsavedChanges ? 'You have unsaved changes.' : 'All changes are saved when you submit.' }}</p>
      <div class="form-actions-buttons">
        <button type="button" class="btn btn-outline-secondary mr-sm-1" @click="onCancel">Cancel</button>
        <button type="submit" class="btn btn-primary" :disabled="saving">
          <span v-if="saving" class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
          {{ saving ? 'Saving…' : (isNew ? 'Create place' : 'Save changes') }}
        </button>
      </div>
    </div>
  </form>

  <ConfirmDialog></ConfirmDialog>
</template>

<script>
import {defineComponent} from 'vue'
import apiService from "@/services/ApiService";
import FileUpload from "primevue/fileupload";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";

export default defineComponent({
  name: "PlaceForm",
  components: {
    FileUpload, InputText, Textarea
  },
  props: {
    id: {
      type: String,
      required: false
    },
    isNew: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      model: {
        id: '',
        name: '',
        image1: '',
        image2: '',
        image3: '',
        description: '',
        latitude: '',
        longitude: '',
        placeTypeId: '',
        placeTypeName: '',
        uploadFile: '',
        orderNumber: ''
      },
      saving: false,
      hasUnsavedChanges: false,
      fieldErrors: {},
      placeTypes: [],
      uploadUrl: ''
    }
  },
  computed: {
    formErrorMessage() {
      return Object.values(this.fieldErrors)[0];
    },
    imagePreviewUrl() {
      return this.model.image1 ? `${apiService.defaults.baseURL}/file/${this.model.image1}` : '';
    }
  },
  methods: {
    onSubmit(event) {
      event.preventDefault();
      if (!this.validateForm()) {
        return;
      }

      this.saving = true;
      let url = '/place/edit';
      let successMessage = 'Place has been updated';
      let errorMessage = 'Failed to update place';
      if(this.isNew){
        url = '/place/add';
        successMessage = 'Place has been added';
        errorMessage = 'Failed to add place';
      }

      const payload = {
        ...this.model,
        latitude: this.hasValue(this.model.latitude) ? Number(this.model.latitude) : null,
        longitude: this.hasValue(this.model.longitude) ? Number(this.model.longitude) : null,
        orderNumber: this.hasValue(this.model.orderNumber) ? Number(this.model.orderNumber) : null
      };

      apiService.post(url, payload)
          .then(() => {
            this.hasUnsavedChanges = false;
            this.$toast.add({
              severity: 'success',
              summary: 'Success',
              detail: successMessage,
              life: 5000
            });
          })
          .catch(error => {
            this.$toast.add({
              severity: 'error',
              summary: errorMessage,
              detail: error.response?.data?.Message || 'Please try again.',
              life: 5000
            });
          })
          .finally(() => {
            this.saving = false;
          });
    },
    onCancel() {
      if (!this.hasUnsavedChanges) {
        this.$router.push('/place');
        return;
      }

      this.$confirm.require({
        message: 'Discard your unsaved place changes?',
        header: 'Discard changes',
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        accept: () => this.$router.push('/place')
      });
    },
    markDirty() {
      this.hasUnsavedChanges = true;
      if (Object.keys(this.fieldErrors).length) {
        this.validateForm();
      }
    },
    validateForm() {
      const errors = {};
      const requiredFields = [
        ['name', 'Name is required.'],
        ['placeTypeId', 'Type is required.'],
        ['description', 'Description is required.']
      ];

      requiredFields.forEach(([field, message]) => {
        if (!String(this.model[field] || '').trim()) {
          errors[field] = message;
        }
      });

      ['latitude', 'longitude'].forEach(field => {
        if (this.hasValue(this.model[field]) && !Number.isFinite(Number(this.model[field]))) {
          errors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} must be a number.`;
        }
      });

      if (this.hasValue(this.model.orderNumber) && (!Number.isInteger(Number(this.model.orderNumber)) || Number(this.model.orderNumber) < 0)) {
        errors.orderNumber = 'Display order must be a whole number of 0 or more.';
      }

      this.fieldErrors = errors;
      return Object.keys(errors).length === 0;
    },
    hasValue(value) {
      return value !== '' && value !== null && value !== undefined;
    },
    onUploadSuccess(event){
      const response = JSON.parse(event.xhr.response);
      this.model.image1 = response.fileName;
      this.hasUnsavedChanges = true;

      this.$toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Image uploaded.',
        life: 5000
      });
    },
    onUploadError(event){
      let response = {};
      try {
        response = JSON.parse(event.xhr.response);
      } catch (_) {
        response = {};
      }
      this.$toast.add({
        severity: 'error',
        summary: 'Failed to upload image',
        detail: response.Message || 'Please choose a supported image and try again.',
        life: 5000
      });
    },
    getPlaceById(id) {
      apiService.get(`/place/${id}`)
          .then(response => {
            this.model = {
              ...this.model,
              ...response.data,
              uploadFile: ''
            };
            this.hasUnsavedChanges = false;
          }).catch(error => {
        this.$toast.add({
          severity: 'error',
          summary: 'Failed to get place',
          detail: error.response?.data?.Message || 'Please try again.',
          life: 5000
        });
      });
    },
    getPlaceTypes() {
      apiService.get('/place/types')
          .then(response => {
            this.placeTypes = response.data || [];
          }).catch(error => {
        this.$toast.add({
          severity: 'error',
          summary: 'Failed to get place types',
          detail: error.response?.data?.Message || 'Please try again.',
          life: 5000
        });
      });
    }
  },
  mounted() {
    this.uploadUrl = `${apiService.defaults.baseURL}/file/upload`;
    this.getPlaceTypes();
    if(!this.isNew){
      this.getPlaceById(this.id);
    }
  }
})
</script>

<style scoped>
.place-form {
  max-width: 1280px;
}

.form-required-note {
  color: #5f6570;
  font-size: 0.875rem;
  margin: 0 0 1rem;
}

.form-required-note span,
.required-indicator {
  color: #c62828;
  font-weight: 700;
}

.form-alert {
  align-items: flex-start;
  background: #fff1f1;
  border: 1px solid #f5c2c7;
  border-radius: 0.5rem;
  color: #9d2525;
  display: flex;
  flex-direction: column;
  font-size: 0.9rem;
  gap: 0.2rem;
  margin-bottom: 1rem;
  padding: 0.8rem 1rem;
}

.place-form-section {
  background: #ffffff;
  border: 1px solid #e2e6ec;
  border-radius: 0.65rem;
  box-shadow: 0 0.15rem 0.65rem rgba(58, 59, 69, 0.05);
  height: 100%;
  padding: 1.5rem;
}

.section-heading {
  align-items: flex-start;
  border-bottom: 1px solid #edf0f4;
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
}

.section-heading h2 {
  color: #2b2f36;
  font-size: 1.15rem;
  font-weight: 600;
  margin: 0;
}

.section-eyebrow {
  color: #426cc6;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin: 0 0 0.25rem;
}

.section-heading > .pi {
  color: #426cc6;
  font-size: 1.2rem;
  padding-top: 0.25rem;
}

.form-group label,
.coordinate-heading > span {
  color: #343a40;
  font-size: 0.9rem;
  font-weight: 600;
}

.form-help,
.coordinate-heading small {
  color: #6c757d;
  display: block;
  font-size: 0.78rem;
  line-height: 1.35;
  margin-top: 0.35rem;
}

.upload-help {
  margin-bottom: 0.65rem;
}

.field-error {
  color: #c62828;
  display: block;
  font-size: 0.8rem;
  margin-top: 0.35rem;
}

.coordinate-heading {
  margin: 0 0 1rem;
}

.place-image-preview {
  align-items: center;
  color: #5f6570;
  display: flex;
  font-size: 0.8rem;
  gap: 0.65rem;
  margin-top: 0.85rem;
}

.place-image-preview img {
  border: 1px solid #e2e6ec;
  border-radius: 0.4rem;
  height: 3rem;
  object-fit: cover;
  width: 5.25rem;
}

.form-actions {
  align-items: center;
  border-top: 1px solid #e2e6ec;
  display: flex;
  justify-content: space-between;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
}

.form-actions-note {
  color: #6c757d;
  font-size: 0.85rem;
  margin: 0;
}

.form-actions-buttons {
  display: flex;
}

@media (max-width: 767.98px) {
  .place-form-section {
    padding: 1.15rem;
  }

  .form-actions {
    align-items: stretch;
    flex-direction: column;
    gap: 1rem;
  }

  .form-actions-buttons {
    justify-content: flex-end;
  }
}
</style>
